import classNames from 'classnames';
import { difference, filter, find, includes, isNil, map, orderBy, uniqBy } from 'lodash';
import React, { FunctionComponent, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaPrint, FaSpinner } from 'react-icons/fa';
import { Redirect, Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader, Nav, Navbar, NavbarBrand, NavItem, NavLink, Spinner, UncontrolledDropdown } from 'reactstrap';
import { gql, useQuery } from 'urql';
import { DateTime, Loading, LoadingUnitIdentifier, MatchPath, TerminalHandlingHarbourOfficeAuthorization } from '.';
import { useCurrentUser } from '../hooks';
import useDangerousGoodsData from '../hooks/useDangerousGoodsData';
import styled from '../themes/themed-styled-components';
import { RoleName } from '../types';
import { adrClassesList, numericTierFromBrt, unNumbersList } from '../util';
import { DangerousGoods, HacpackQuery, HacpackQueryHandlingArguments, Handling, HandlingAttachment, Terminal } from './../types/graph';
import AssetViewer from './AssetViewer';
import DataTable from './DataTable';
import EmergencyCardViewer, { EmergencyCardViewerImperativeHandle } from './EmergencyCardViewer';
import HandlingForm from './Handling/HandlingForm';
import HandlingPrescriptions from './Handling/HandlingPrescriptions';
import HandlingQsaApproval from './Handling/HandlingQsaApproval';
import HandlingQsaNotes from './Handling/HandlingQsaNotes';
import HandlingsDeleteButton, { HandlingWithIdAndUnitNumber } from './Handling/HandlingsDeleteButton';
import HandlingRiskAssessment from './HandlingRiskAssessment';
import PrivateRoute from './PrivateRoute';
import { QueryLoaderContext } from './QueryLoader';
import TerminalHandlingPlacardsAndMarkings from './TerminalHandlingPlacardsAndMarkings';
import TerminalHandlingPosition from './TerminalHandlingPosition';
import TerminalHandlingSegregation from './TerminalHandlingSegregation';
import { GridLayout } from './ui';

const Container = styled.div`
padding: .5em;
dl {
    max-width: 100%;
}
`;

type HandlingDetailProps = {
    terminal: Terminal,
    handling: Handling,
    disabled?: boolean,
    onDelete?: (() => void) | null,
    onClose?: (() => void) | null
};

type HandlingDetailMainProps = {
    handling: Handling,
    terminal: Terminal,
}

type HandlingDetailPortAuthorityAuthorizationProps = {
    handling: Handling
}

type HandlingDetailChemistDocumentProps = {
    chemistDocument: HandlingAttachment
}


const HandlingDetail = ({ handling, terminal }: HandlingDetailMainProps) => {
    const { t } = useTranslation('common');

    //const [, setSortField] = useState<string | undefined>(undefined);
    //const [, setSortAsc] = useState<boolean>(true);

    const { url } = useRouteMatch();

    //const { columns } = useDangerousGoodsData();

    //const onSort = useCallback((column: TableColumn<DangerousGoods>, sortDirection: 'asc' | 'desc') => {
    //    setSortField(column.sortField);
    //    setSortAsc(sortDirection === 'asc');
    //}, [setSortField, setSortAsc]);

    const { loadingUnitIdentifier, checkedIn, checkInDate, checkedOut, checkOutDate, dangerousGoods, shippingAgencyOrCarrier, unboardingShip,
        originVoyage, boardingShip, destinationVoyage, area, bay, row, tier, prescriptions, terminalName, idSerchim } = handling;


    const formattedTier = terminalName === 'TERMINAL DEL GOLFO' ? numericTierFromBrt(tier) : tier

    const positionForm = terminal.name !== 'MARTERNERI' && terminal.port !== "TRIESTE" ? null : <div><TerminalHandlingPosition terminal={terminal} handling={handling} /></div>;
    const harbourOfficeAuthorization = useMemo(() => {
        return <div className='p-2'><TerminalHandlingHarbourOfficeAuthorization terminal={terminal} handling={handling} /></div>

        //return null;
    }, [handling, terminal])

    //if (dangerousGoods === null) {
    //    return <Loading />;
    //}

    const dgs = dangerousGoods?.map(({ adrClass, unNumber, limitedQuantity }) => ({ adrClass: adrClass ?? undefined, unNumber, limitedQuantity })) ?? []

    return <Container>
        <dl>
            {
                loadingUnitIdentifier?.trim() !== idSerchim?.toString()
                    ? <><dt>{t('Sigla')}</dt><dd>{loadingUnitIdentifier}</dd></>
                    : <><dt>{t('N� Autorizzazione')}</dt><dd>{idSerchim}</dd></>
            }

            {checkedIn === true && checkInDate !== undefined && checkInDate !== null
                ? <><dt>{t('Data d\'ingresso')}</dt><dd><DateTime date={checkInDate} /></dd></>
                : null}

            {checkedOut === true && checkedOut !== undefined && checkedOut !== null
                ? <><dt>{t('Data di uscita')}</dt><dd><DateTime date={checkOutDate} /></dd></>
                : null}

            {shippingAgencyOrCarrier !== undefined && shippingAgencyOrCarrier !== null
                ? <><dt>{t('Agenzia')}</dt><dd>{shippingAgencyOrCarrier}</dd></>
                : null}

            {unboardingShip !== undefined && unboardingShip !== null
                ? <><dt>{t('Nave di sbarco')}</dt><dd>{unboardingShip}</dd></>
                : null}

            {originVoyage !== undefined && originVoyage !== null
                ? <><dt>{t('Viaggio d\'origine')}</dt><dd>{originVoyage}</dd></>
                : null}

            {boardingShip !== undefined && boardingShip !== null
                ? <><dt>{t('Nave d\'imbarco')}</dt><dd>{boardingShip}</dd></>
                : null}

            {destinationVoyage !== undefined && destinationVoyage !== null
                ? <><dt>{t('Viaggio di destinazione')}</dt><dd>{destinationVoyage}</dd></>
                : null}
        </dl>


        {checkedIn === true && checkedOut === false
            ? <>
                <h4>{t('Posizione')}</h4>
                <dl className={'row'}>
                    <dt className={'col-sm-1'}>{t('Area')}</dt><dd className={'col-sm-2'}>{area}</dd>
                    <dt className={'col-sm-1'}>{t('Baia')}</dt><dd className={'col-sm-2'}>{bay}</dd>
                    <dt className={'col-sm-1'}>{t('Fila')}</dt><dd className={'col-sm-2'}>{row}</dd>
                    <dt className={'col-sm-1'}>{t('Tiro')}</dt><dd className={'col-sm-2'}>{formattedTier}</dd>
                </dl>
            </>
            : null}

        <HandlingPrescriptions handling={handling} />

        {terminal.name === 'TERMINAL DEL GOLFO' && <HandlingQsaNotes handling={handling} />}

        <div className="d-flex flex-wrap gap-2 align-items-baseline"><h3>{t('Merci Pericolose')} <small>({dgs.length})</small></h3> <Link to={`${url}/merci-pericolose`}>{t('Dettaglio')}</Link></div>

        <div className="d-flex flex-wrap gap-1">
            <dl className="p-2 d-flex gap-2 flex-grow-1 flex-wrap mb-0">
                <dt>{t('UN')}</dt>
                <dd className="d-flex gap-1 flex-wrap">{unNumbersList(dgs).map(([cls, lq]) => lq ? <span title={t('Limited Quantity').toString()} key={cls}>{cls}</span> : <strong key={cls}>{cls}</strong>)}</dd>
            </dl>
            <dl className="p-2 d-flex gap-2 flex-grow-1 flex-wrap mb-0">
                <dt>{t('Classi')}</dt>
                <dd className="d-flex gap-1 flex-wrap">{adrClassesList(dgs).map(([cls, lq]) => lq ? <span title={t('Limited Quantity').toString()} key={cls}>{cls}</span> : <strong key={cls}>{cls}</strong>)}</dd>
            </dl>
        </div>

        <HandlingRiskAssessment handling={handling} />


        {
            // @ts-ignore
            (terminal.name === 'REEFER TERMINAL' || terminal.name === 'VADO GATEWAY') && <HandlingQsaApproval handling={handling} terminal={terminal} />
        }

        {/*{((dangerousGoods?.length ?? 0) === 0)*/}
        {/*    ? <Alert color='warning'>{t('terminal.data.noData')}</Alert>*/}
        {/*    : <DataTable<DangerousGoods>*/}
        {/*        title={<Fragment>{t('terminal.data.dangerousGoods.name')} <Badge>{dangerousGoods?.length}</Badge></Fragment>}*/}
        {/*        columns={columns as IDataTableColumn<DangerousGoods>[]}*/}
        {/*        data={dangerousGoods as DangerousGoods[]}*/}
        {/*        striped*/}
        {/*        onSort={onSort}*/}
        {/*        defaultSortField={sortField}*/}
        {/*        defaultSortAsc={sortAsc}*/}
        {/*        noDataComponent={<Alert color='warning'>{t('terminal.data.dangerousGoods.noData')}</Alert>}*/}
        {/*    />*/}
        {/*}*/}
        {harbourOfficeAuthorization}
        {positionForm}
    </Container>
}

const handlingSafetyDataSheetUrlsQuery = gql<HacpackQuery, HacpackQueryHandlingArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        safetyDataSheetUrls
    }
}`;

const handlingImdgGateAttachmentsQuery = gql<HacpackQuery, HacpackQueryHandlingArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        imdgGateAttachments {
            id,
            filename,
            description,
            downloadUrl,
            goodsAuthorityId
        }
    }
}`;

const HandlingDangerousGoods = ({ handling, terminal }: HandlingDetailMainProps) => {
    const { t } = useTranslation('common');

    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const [showBatchPrint, setShowBatchPrint] = useState<boolean>(false);
    const [print, setPrint] = useState<boolean>(false);

    const toggleShowBatchPrint = useCallback((() => {
        if (showBatchPrint === false) {
            setShowBatchPrint(true)
            return;
        }

        setShowBatchPrint(false)
        setPrint(false)
    }), [showBatchPrint])

    const { columns } = useDangerousGoodsData();

    const onSort = useCallback((column: TableColumn<DangerousGoods>, sortDirection: 'asc' | 'desc') => {
        setSortField(column.sortField as string);
        setSortAsc(sortDirection === 'asc');
    }, [setSortField, setSortAsc]);

    const { dangerousGoods, imdgGateAttachments } = handling;

    const dangerousGoodsWithAttachments = useMemo(() => {
        if (dangerousGoods === undefined || dangerousGoods === null || imdgGateAttachments === undefined || imdgGateAttachments === null) {
            return dangerousGoods;
        }

        return map(dangerousGoods, (dg) => {
            const { authorityId } = dg;

            if (authorityId === undefined || authorityId === null) {
                return dg;
            }

            const authorityIdNumber = Number(authorityId);

            if (isNaN(authorityIdNumber)) {
                return dg;
            }

            return {
                ...dg,
                imdgGateAttachments: filter(imdgGateAttachments, ['goodsAuthorityId', authorityIdNumber])
            }
        })
    }, [imdgGateAttachments, dangerousGoods]);

    const printableAttachments = useMemo(() => {
        const attachments: Array<BatchPrintAttachment> = []

        dangerousGoodsWithAttachments?.forEach(({ emergencyCardUrl, unNumber, adrClass }) => {
            if (isNil(emergencyCardUrl) || attachments.some(({ url }) => url === emergencyCardUrl)) {
                return
            }

            attachments.push({
                type: 'emergency-card',
                title: `Scheda di emergenza UN ${unNumber} classe ${adrClass}`,
                name: `Scheda di emergenza UN ${unNumber} classe ${adrClass}`,
                url: emergencyCardUrl,
                defaultEnabled: true
            })
        })

        return attachments;

    }, [dangerousGoodsWithAttachments])

    const header = useMemo(() => {
        if (terminal.name !== 'MEDCENTER') {
            return null;
        }
        return <Navbar className="navbar ng-white border-bottom box-shadow" light>
            <div className="d-sm-inline-flex flex-sm-row">

            </div>
            <div className="navbar-right d-sm-inline-flex flex-sm-row" style={{ gap: '.25em' }}>
                <Button onClick={() => toggleShowBatchPrint()}><FaPrint /> {t('Stampa allegati merci...')}</Button>
            </div>
        </Navbar>;
    }, [terminal.name, t, toggleShowBatchPrint])

    const batchPrint = useMemo(() => <div>
        <Modal isOpen={showBatchPrint} toggle={toggleShowBatchPrint}>
            <ModalHeader>{t('Stampa allegati merci')}</ModalHeader>
            <ModalBody>
                <AttachmentsBatchPrint print={print} onComplete={() => setPrint(false)} attachments={printableAttachments} />
            </ModalBody>
            <ModalFooter>
                <Button color="primary" disabled={print} onClick={() => setPrint(true)}>{t('Stampa')}</Button>{' '}
                <Button color="secondary" onClick={toggleShowBatchPrint}>{print ? t('Annulla') : t('Chiudi')}</Button>
            </ModalFooter>
        </Modal>
    </div>, [showBatchPrint, toggleShowBatchPrint, t, print, printableAttachments])

    if (dangerousGoods === undefined) {
        return <Loading />;
    }

    return (dangerousGoods === null || dangerousGoodsWithAttachments?.length === 0)
        ? <Alert color='warning'>{t('terminal.data.noData')}</Alert>
        : <GridLayout header={header}>
            <DataTable<DangerousGoods>
                lastColumnSticky
                noHeader
                columns={columns as TableColumn<DangerousGoods>[]}
                data={dangerousGoodsWithAttachments as DangerousGoods[]}
                striped
                onSort={onSort}
                defaultSortFieldId={sortField}
                defaultSortAsc={sortAsc}
                noDataComponent={<Alert color='warning'>{t('terminal.data.dangerousGoods.noData')}</Alert>}
            />
            {batchPrint}
        </GridLayout>

}

type BatchPrintAttachment = {
    type: 'emergency-card',
    title?: ReactNode,
    name: string,
    url: string,
    defaultEnabled: boolean
}

type AttachmentsBatchPrintProps = {
    attachments: Array<BatchPrintAttachment>,
    print: boolean,
    onComplete?: () => void
}

const AttachmentsBatchPrint: FunctionComponent<AttachmentsBatchPrintProps> = ({ attachments, print, onComplete }) => {

    const [printed, setPrinted] = useState<string[]>([])

    useEffect(() => {
        if (print === false) {
            return
        }

        setPrinted([])
    }, [print])

    const handlePrinted = useCallback((url: string) => {
        const toPrint = attachments.map(({ url }) => url)
        //const printed = printedRef.current;
        //printed.push(url)

        setPrinted((printed) => {
            const prt = [...printed, url]

            if (difference(toPrint, prt).length === 0) {
                onComplete?.()
            }

            return prt
        })

    }, [attachments, onComplete])

    return <>
        {orderBy(attachments, 'name', 'asc').map((attachment) => {
            const done = printed.includes(attachment.url)

            const color = done ? 'green' : undefined

            return <div key={attachment.url} style={{ display: 'flex', gap: '1em', color }}>
                <div>{attachment.title}</div>
                {done === true && <FaCheck /> || print === true && <FaSpinner style={{ animation: 'spin 1s infinite linear' }} />}
                <div style={{ position: 'absolute', height: 0, width: 0, overflow: 'hidden' }}>
                    <AttachmentPrint print={print} onPrinted={() => handlePrinted(attachment.url)} attachment={attachment} />
                </div>
            </div>
        })}
    </>
}

type AttachmentPrintProps = {
    attachment: BatchPrintAttachment,
    print: boolean
    onPrinted?: (success: boolean) => void
}

const AttachmentPrint: FunctionComponent<AttachmentPrintProps> = ({ attachment, print, onPrinted }) => {

    const ref = useRef<EmergencyCardViewerImperativeHandle>(null)
    const onPrintedRef = useRef<AttachmentPrintProps['onPrinted']>()

    useEffect(() => { onPrintedRef.current = onPrinted }, [onPrinted])

    useEffect(() => {
        if (print === false) {
            return
        }

        let mount = true

        const cb = (success: boolean) => {
            if (mount === false) {
                return
            }

            onPrintedRef.current?.(success)
        }

        ref.current?.print().then(() => cb(true)).catch(() => cb(false))

        return () => {
            mount = false
        }
    }, [print])

    return <EmergencyCardViewer ref={ref} title={attachment.name} url={attachment.url} />
}

type DocumentViewerProps = {
    url: string
}
const DocumentViewer = ({ url }: DocumentViewerProps) => <AssetViewer src={url} />;

const HandlingDetailPortAuthorityAuthorization = ({ handling }: HandlingDetailPortAuthorityAuthorizationProps) => {
    return <DocumentViewer url={handling.portAuthorityAuthorization?.downloadUrl as string} />;
};

const HandlingDetailChemistDocument = ({ chemistDocument }: HandlingDetailChemistDocumentProps) => {
    return <DocumentViewer url={chemistDocument.downloadUrl as string} />;
};

const HandlingDetailPage = ({ terminal, handling: handlingFromProps, disabled = false, onDelete = null, onClose = null }: HandlingDetailProps) => {

    const { state, ...location } = useLocation<{ parentUrl: string }>();

    const { reload } = useContext(QueryLoaderContext);

    const { url } = useRouteMatch();
    const history = useHistory();

    const { hasRoleForTerminal, isSuperuser } = useCurrentUser();

    const [ deleted, setDeleted ] = useState<boolean>(false)

    const [imdgResult] = useQuery({
        query: handlingImdgGateAttachmentsQuery,
        variables: { id: handlingFromProps?.id as number },
        pause: handlingFromProps?.id === undefined || terminal.authorityType !== 'IMDG-GATE'
    });

    const { data: imdgData } = imdgResult;

    const { id, idSerchim } = handlingFromProps

    const [result] = useQuery({
        query: handlingSafetyDataSheetUrlsQuery,
        variables: { id: handlingFromProps.id! },
        pause: id === undefined || idSerchim === null
    });

    const { data: sdsData, fetching: sdsFetching } = result;

    const handling = useMemo(() => ({ ...handlingFromProps, safetyDataSheetUrls: sdsData?.handling?.safetyDataSheetUrls, imdgGateAttachments: imdgData?.handling?.imdgGateAttachments }), [handlingFromProps, imdgData]);

    //const multimodal = useMemo(() => find(handling?.imdgGateAttachments, (h) => { h.description == "Multimodale" })??null, [handling])

    const closeButton = useMemo(() => (onClose ?? null) === null ? null : (<Button className="close" aria-label="Close" onClick={() => onClose?.()}>
        <span aria-hidden="true">&times;</span>
    </Button>), [onClose]);

    const onNameClicked = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((ev) => {
        ev.preventDefault();
        history.push(url);
    }, [history, url])

    const sds = useMemo(() => {
        const safetyDataSheetUrls = sdsData?.handling?.safetyDataSheetUrls ?? null

        if (safetyDataSheetUrls === null) {
            return null;
        }

        return safetyDataSheetUrls.map((ds) => [decodeURI((new URL(ds)).pathname.split('/').at(-1)!), ds] as const);
    }, [sdsData]);

    const canEditHandling = useMemo(() => {
        if (isSuperuser
            ||
            (
                (includes(['REBORA', 'MEDCENTER', 'VECON', 'SAN CATALDO', 'SINTERMAR'], terminal.name) || terminal.port === 'TRIESTE')
                &&
                (
                    (hasRoleForTerminal(RoleName.USER, terminal.id as number) && terminal.name !== 'SINTERMAR')
                    ||
                    hasRoleForTerminal(RoleName.ADMIN, terminal.id as number)
                )
            )
        ) {
            return true;
        }

        return false;
    }, [terminal, hasRoleForTerminal, isSuperuser]);

    const canDeleteHandling = useMemo(() => {
        if (isSuperuser) {
            return true;
        }

        const { name, id, port } = terminal;

        if (terminal.name === 'TERMINAL DEL GOLFO') {
            return hasRoleForTerminal(RoleName.QSA, id as number)
        }
        if (terminal.name === 'VADO GATEWAY' || name === 'REEFER TERMINAL') {
            return hasRoleForTerminal(RoleName.OPERATIVE_MANAGER, id as number)
        }

        return canEditHandling && (includes(['REBORA', 'SAN CATALDO', 'SINTERMAR'], name) || port === 'TRIESTE')
    }, [isSuperuser, terminal, canEditHandling, hasRoleForTerminal]);

    const handleDelete = useCallback(() => {
        setDeleted(true)
        onDelete?.()
    }, [onDelete])

    const href = useMemo(() => url.replace(/^\/*/, ''), [url]);

    const header = useMemo(() => <header>
        <Navbar className="navbar ng-white border-bottom box-shadow pb-0" light>
            <NavbarBrand href="#" onClick={onNameClicked}><LoadingUnitIdentifier identifier={handling?.loadingUnitIdentifier as string} /></NavbarBrand>
            <div style={{ display: 'flex', gap: '.5em' }}>
                {canEditHandling ? <MatchPath to={`${url}/modifica`} render={(to, match) => <Button className={classNames({ disabled: match !== null })} tag={Link} to={to}>{"Modifica"}</Button>} /> : null}
                {canDeleteHandling ? <HandlingsDeleteButton handling={handling as HandlingWithIdAndUnitNumber} disabled={disabled || deleted} onAfterDelete={handleDelete} /> : null}
            </div>
            <div className="d-flex flex-row flex-nowrap navbar-left">
                <Nav tabs className="flex-row flex-nowrap" style={{ marginBottom: -1 }}>

                    {(!isNil(handling?.chemistPrescriptionsUrl) ? <MatchPath to={`${url}/parere-chimico-di-porto`} render={(to, match) =>
                        <NavItem className={classNames({ active: match })}>
                            <NavLink tag="a" href={handling!.chemistPrescriptionsUrl!} className={classNames({ active: match, 'nav-link': true })} onClick={(ev) => {
                                ev.preventDefault();
                                history.push(to.toString());
                            }}>{"Parere del Chimico di Porto"}</NavLink>
                        </NavItem>

                    } /> : null)}

                    {(!isNil(handling?.dangerousGoods) && handling.dangerousGoods.length > 0 ? <MatchPath to={`${url}/merci-pericolose`} render={(to, match) =>
                        <NavItem className={match ? 'active' : ''}>
                            <NavLink tag={Link} className={classNames({ active: match, 'nav-link': true })} to={to}>{"Merci pericolose"}{' '}<Badge>{handling?.dangerousGoods?.length ?? 0}</Badge></NavLink>
                        </NavItem>
                    } /> : null)}

                    {(!isNil(handling?.terminalName) && handling.terminalName === 'TDT' ? <MatchPath to={`${url}/segregazione`} render={(to, match) =>
                        <NavItem className={match ? 'active' : ''}>
                            <NavLink tag={Link} className={classNames({ active: match, 'nav-link': true })} to={to}>{"Segregazione/Test posizioni"}{' '}{/*<Badge>{handling?.dangerousGoods?.length ?? 0}</Badge>*/}</NavLink>
                        </NavItem>
                    } /> : null)}

                    {(includes(['LSCT', 'TERMINAL DEL GOLFO'], terminal.name) === false && handling?.dangerousGoods !== undefined && handling?.dangerousGoods !== null && handling?.dangerousGoods?.length > 0 ? <MatchPath to={`${url}/etichette`} render={(to, match) =>
                        <NavItem className={classNames({ active: match })}>
                            <NavLink tag={Link} className={classNames({ active: match, 'nav-link': true })} to={to}>{"Etichette"}</NavLink>
                        </NavItem>
                    } /> : null)}

                    {(!isNil(handling?.portAuthorityAuthorization) ? <MatchPath to={`${url}/apmp1`} render={(to, match) =>
                        <NavItem className={match ? 'active' : ''}>
                            <NavLink tag={Link} className={classNames({ active: match, 'nav-link': true })} to={to}>{"AP-MP1"}</NavLink>
                        </NavItem>
                    } /> : null)}

                    {((handling?.chemistDocuments ?? []).length > 0 ? <MatchPath to={`${url}/pareri-chimico-di-porto`} render={(to, match) =>
                        <UncontrolledDropdown tag={"li"} className={"nav-item"}>
                            <DropdownToggle tag="a" href="#" className={classNames({ active: match, 'nav-link': true })} onClick={(ev) => ev.preventDefault()} caret>{"Pareri del Chimico di Porto"}</DropdownToggle>
                            <DropdownMenu>
                                {map(uniqBy(handling?.chemistDocuments, 'documentTitle'), ({ documentTitle, downloadUrl }, i) =>
                                (<MatchPath key={i} to={`${to}/${documentTitle?.toLocaleLowerCase()}`} render={(subto, submatch) =>
                                    <DropdownItem tag="a" className={submatch ? 'active' : ''} href={downloadUrl ?? undefined} onClick={(ev) => {
                                        ev.preventDefault();
                                        history.push(subto.toString());
                                    }}>
                                        {documentTitle}
                                    </DropdownItem>
                                } />)
                                )}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    } /> : null)}



                    {((sds !== null || sdsFetching) && <MatchPath to={`${url}/sds`} render={(to, match) =>
                        (sds?.length ?? 0) > 0 ?
                            <UncontrolledDropdown tag={"li"} className={"nav-item"}>
                                <DropdownToggle tag="a" href={`${href}/sds`} disabled={sds!.length === 0} className={classNames({ active: match, 'nav-link': true })} onClick={(ev) => ev.preventDefault()} caret>{"Schede di Dati di Sicurezza"} <Badge>{sds!.length}</Badge></DropdownToggle>
                                <DropdownMenu>
                                    {sds!.map(([filename, surl], i) =>
                                    (<MatchPath key={filename} to={`${to}/${filename}`} render={(subto, submatch) =>
                                        <DropdownItem tag="a" className={classNames({ active: submatch })} href={`${surl}`} onClick={(ev) => {
                                            ev.preventDefault();
                                            history.push(subto.toString());
                                        }}>
                                            {filename}
                                        </DropdownItem>
                                    } />)
                                    )}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                            :
                            <NavItem className={classNames({ active: match })}>
                                <NavLink tag="a" href={`${href}/sds`} className={classNames({ active: match, 'nav-link': true, disabled: true, })} title="Nessun file trovato.">{"Schede di Dati di Sicurezza"} {sdsFetching ? <Spinner type="border" color="secondary" size="sm" /> : <Badge>0</Badge>}</NavLink>
                            </NavItem>
                    } />)}
                </Nav>
            </div>
            <div style={{ flex: 1 }}></div>
            <div className="navbar-right">
                <div style={{ display: 'flex', gap: '0.5em' }}>
                    {closeButton}
                </div>
            </div>

        </Navbar>
    </header>, [onNameClicked, handling, canEditHandling, url, canDeleteHandling, disabled, deleted, handleDelete, terminal.name, sds, sdsFetching, closeButton, history, href]);

    const handlingForm = useMemo(() => {
        const editableFields: Array<keyof Handling> | undefined = handling.checkedIn === true ? ['dangerousGoods'] : undefined;

        if ((handling?.ship ?? '') === '') {
            editableFields?.push('ship');
        }

        if ((handling?.type ?? '') === '') {
            editableFields?.push('type');
        }


        if ((handling?.ship ?? '') === '') {
            editableFields?.push('ship');
        }

        if ((handling?.loadingUnitSize ?? '') === '') {
            editableFields?.push('loadingUnitSize');
        }

        return <HandlingForm handling={handling} editableFields={editableFields} onSaved={(handling) => { reload(); history.push(url); }} onCancel={() => { history.push(url) }} />
    }, [handling, history, reload, url]);

    return <Switch location={{ state: { ...(state ?? {}), parentUrl: url }, ...location }}>

        <PrivateRoute exact path={`${url}/modifica`} >{handlingForm}</PrivateRoute>

        <PrivateRoute path={`${url}`}>

            <GridLayout header={header}>
                <Switch>
                    <PrivateRoute exact path={`${url}`}>
                        <HandlingDetail terminal={terminal} handling={handling as Handling} />
                    </PrivateRoute>

                    <PrivateRoute exact path={`${url}/merci-pericolose`}>
                        <HandlingDangerousGoods terminal={terminal} handling={handling as Handling} />
                    </PrivateRoute>

                    <PrivateRoute path={`${url}/segregazione`} >
                        <TerminalHandlingSegregation handlingId={handling.id as number} />
                    </PrivateRoute>

                    <PrivateRoute path={`${url}/etichette`} >
                        <TerminalHandlingPlacardsAndMarkings handlingId={handling.id as number} />
                    </PrivateRoute>

                    <PrivateRoute path={`${url}/apmp1`} >
                        {!isNil(handling?.portAuthorityAuthorization) ? <HandlingDetailPortAuthorityAuthorization handling={handling as Handling} /> : <Redirect to={url} />}
                    </PrivateRoute>

                    <PrivateRoute exact path={`${url}/pareri-chimico-di-porto`} >
                        {((handling?.chemistDocuments ?? []).length > 0) ? <Redirect to={`${url}/pareri-chimico-di-porto/${handling?.chemistDocuments?.[0]?.documentTitle?.toLocaleLowerCase()}`} /> : <Redirect to={url} />}
                    </PrivateRoute>

                    <PrivateRoute path={`${url}/pareri-chimico-di-porto/:title`} render={({ match: { params } }) => {
                        const title = params['title'] ?? null;

                        const chemistDocument = title !== null ? find(handling?.chemistDocuments, (cd) => cd.documentTitle?.toLowerCase() === title) ?? null : null;

                        if (chemistDocument === null) {
                            const redirect = `${url}/pareri-chimico-di-porto`;
                            return <Redirect to={redirect} />;
                        }

                        return <HandlingDetailChemistDocument chemistDocument={chemistDocument} />
                    }} />

                    <PrivateRoute exact path={`${url}/parere-chimico-di-porto`}>
                        {handling !== undefined && !isNil(handling.chemistPrescriptionsUrl)
                            ? <DocumentViewer url={handling.chemistPrescriptionsUrl} />
                            : <Redirect to={url} />}
                    </PrivateRoute>

                    {(sds !== null) &&
                        <PrivateRoute exact path={`${url}/sds`}>
                            <div className="p-3">
                                <h1>Schede di Dati di Sicurezza</h1>
                                {sds.length > 0
                                    ? <ul>{
                                        sds.map(([filename, surl]) => <li><a href={surl} onClick={(ev) => {
                                            ev.preventDefault();
                                            history.push(`${url}/sds/${filename}`);
                                        }}>{filename}</a></li>)
                                    }</ul>
                                    : <p>Nessun file trovato.</p>
                                }
                            </div>
                        </PrivateRoute>
                    }

                    {(sds?.length ?? 0) > 0 && (
                        <PrivateRoute path={`${url}/sds/:filename`} render={({ match: { params } }) => {
                            const filename = params['filename'] ?? '';

                            const surl = Object.fromEntries(sds ?? [])[filename] ?? null

                            if (surl === null) {
                                return <Redirect to={`${url}/sds`} />;
                            }

                            return <DocumentViewer url={surl} />
                        }} />
                    )}

                </Switch>
            </GridLayout>
        </PrivateRoute>
    </Switch>
}

export default HandlingDetailPage;
